import React from 'react';
import { Modal, Box, Button, Typography, Tabs, Tab, Tooltip, IconButton, LinearProgress, Alert } from '@mui/material';
import { Close, Email, People, ArrowForward, Send } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { SendEmailModalProps } from '../hooks/useSendEmailModal';
import { EmailDetails } from './EmailDetails';
import EmailResponseSummary from './EmailResponseSummary';
import EmailTemplatePreview from './EmailTemplatePreview';
import EmailSendingModal from './EmailSendingModal';
import { DateRangeRsuite } from '../../../components';
import { formatUnixTimestamp } from '../../../library';
import { useSendEmailModalWrapper } from '../hooks/useSendEmailModalWrapper';
import { COLORS, MESSAGES } from '../constants/emailConstants';

export const SendEmailModalWrapper: React.FC<SendEmailModalProps> = ({
  open,
  onClose,
  initialPageType = '',
  recipients = [],
  onUpdateEmail,
  isEditRecipient,
  setIsEditRecipient,
  removeRecipient,
}) => {
  const {
    activeTab,
    hasUnsavedChanges,
    templatePreviewReady,
    customContentForThisEmail,
    resetTemplatePreview,
    emailSendingProgress,
    templateId,
    showDateRange,
    loading,
    pageType,
    dateRange,
    modalHeader,
    responseSummary,
    handleTabChange,
    handleCloseWrapperModal,
    handleTemplateSaveStatusChange,
    handleUnsavedChangesChange,
    handleGetCurrentTemplateContent,
    handleGetShowUnsavedChangesPopup,
    handleConfirmTemplateUpdate,
    handleCancelTemplateUpdate,
    handlePreviewReady,
    handleNextClick,
    handleSendClick,
    handleDateRangeChange,
    handleCloseResponseSummary,
    setCustomContentForThisEmail,
  } = useSendEmailModalWrapper({
    open,
    onClose,
    initialPageType,
    recipients,
    onUpdateEmail,
    isEditRecipient,
    setIsEditRecipient,
    removeRecipient,
  });

  return (
    <>
      <Modal open={open} aria-labelledby="send-email-modal" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '98vw', sm: '95vw', md: '90vw', lg: '80vw', xl: '70vw' },
            maxWidth: { xs: 'none', sm: 1000, md: 1200, lg: 1400 },
            height: { xs: '90vh', sm: '95vh', md: '92vh', lg: '88vh' },
            bgcolor: 'background.paper',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            borderRadius: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              background: COLORS.PRIMARY_GRADIENT,
              color: 'white',
              p: { xs: 1, sm: 1.25 },
              boxShadow: '0 4px 20px rgba(145, 27, 124, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Email sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography component="h2" id="send-email-modal" sx={{ m: 0, fontSize: '15px', fontWeight: 600, mb: 0.25 }}>
                  {modalHeader}
                </Typography>
                <Typography sx={{ fontSize: '12px', opacity: 0.9, fontWeight: 400 }}>
                  {templatePreviewReady ? 'Manage email template and recipients' : MESSAGES.INFO.TEMPLATE_LOADING_TITLE}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseWrapperModal}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 0.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'sticky', top: { xs: 64, sm: 72 }, zIndex: 2, mt: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="email modal tabs"
              variant={window.innerWidth < 600 ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                  background: COLORS.PRIMARY_GRADIENT,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  minHeight: 40,
                  px: 1.5,
                  py: 0.75,
                  color: 'text.secondary',
                  transition: 'all 0.3s ease',
                  '&:hover': { color: COLORS.PRIMARY_COLOR, backgroundColor: COLORS.PRIMARY_BACKGROUND },
                  '&.Mui-selected': { color: COLORS.PRIMARY_COLOR, fontWeight: 600 },
                },
              }}
            >
              <Tab icon={<Email sx={{ fontSize: 16 }} />} iconPosition="start" label="Email Template" sx={{ gap: 0.75 }} />
              <Tab icon={<People sx={{ fontSize: 16 }} />} iconPosition="start" label="Recipients" sx={{ gap: 0.75 }} />
            </Tabs>
            {!templatePreviewReady && (
              <LinearProgress
                color="primary"
                sx={{
                  height: 3,
                  borderRadius: 0,
                  '& .MuiLinearProgress-bar': { borderRadius: 0 },
                }}
              />
            )}
          </Box>

          {/* Date Range */}
          {showDateRange && (
            <Box
              sx={{
                position: 'sticky',
                top: { xs: 104, sm: 112 },
                zIndex: 2,
                bgcolor: 'background.paper',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.25 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(to bottom, rgba(145, 27, 124, 0.02), transparent)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Box sx={{ width: 3, height: 14, borderRadius: '2px', background: COLORS.PRIMARY_GRADIENT }} />
                <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
                  Select Date Range
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <DateRangeRsuite value={[dateRange.from, dateRange.to]} onChange={handleDateRangeChange} showHeader />
                {dateRange.from && dateRange.to && (
                  <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: COLORS.PRIMARY_LIGHT, color: COLORS.PRIMARY_COLOR, fontSize: '13px', fontWeight: 500 }}>
                    {formatUnixTimestamp(dateRange.from)} to {formatUnixTimestamp(dateRange.to)}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Tab Content — keep EmailTemplatePreview mounted while modal is open so template refs / getCurrentContent stay valid on the Recipients tab */}
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: activeTab === 0 ? 'flex' : 'none',
                flex: activeTab === 0 ? 1 : 0,
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'auto',
                p: activeTab === 0 ? { xs: 0.5, sm: 0.5 } : 0,
              }}
              aria-hidden={activeTab !== 0}
            >
              <EmailTemplatePreview
                templateId={templateId}
                onTemplateSaveStatusChange={handleTemplateSaveStatusChange}
                onUnsavedChangesChange={handleUnsavedChangesChange}
                onGetCurrentTemplateContent={handleGetCurrentTemplateContent}
                onGetShowUnsavedChangesPopup={handleGetShowUnsavedChangesPopup}
                onConfirmTemplateUpdate={handleConfirmTemplateUpdate}
                onCancelTemplateUpdate={handleCancelTemplateUpdate}
                resetOnClose={resetTemplatePreview}
                customContentForThisEmail={customContentForThisEmail}
                onSetCustomContentForThisEmail={setCustomContentForThisEmail}
                onPreviewReady={handlePreviewReady}
              />
            </Box>
            <Box
              sx={{
                display: activeTab === 1 ? 'block' : 'none',
                flex: activeTab === 1 ? 1 : 0,
                minHeight: 0,
                overflow: 'auto',
                py: 0.5,
              }}
              aria-hidden={activeTab !== 1}
            >
              {!templatePreviewReady && activeTab === 1 && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.75 }} icon={false}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {MESSAGES.INFO.TEMPLATE_LOADING_BANNER}
                  </Typography>
                </Alert>
              )}
              <EmailDetails
                recipients={recipients}
                onUpdateEmail={onUpdateEmail}
                setIsEditRecipient={setIsEditRecipient}
                removeRecipient={removeRecipient}
                addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
              />
            </Box>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(to top, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
              backdropFilter: 'blur(10px)',
              py: { xs: 1, sm: 1.25 },
              px: { xs: 1.5, sm: 2 },
              display: 'flex',
              justifyContent: 'flex-end',
              gap: { xs: 1, sm: 1.5 },
              flexDirection: { xs: 'column', sm: 'row' },
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseWrapperModal}
              startIcon={<Close sx={{ fontSize: 16 }} />}
              sx={{
                minWidth: { xs: '100%', sm: 100 },
                order: 1,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                borderRadius: 1.5,
                borderColor: 'divider',
                '&:hover': { borderColor: COLORS.PRIMARY_COLOR, bgcolor: COLORS.PRIMARY_BACKGROUND },
              }}
            >
              Cancel
            </Button>
            {activeTab === 0 ? (
              <Box component="span" sx={{ order: 2 }}>
                <Tooltip
                  title={
                    !templatePreviewReady
                      ? MESSAGES.INFO.LOADING_PREVIEW
                      : hasUnsavedChanges
                        ? MESSAGES.INFO.UNSAVED_CHANGES_NEXT
                        : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleNextClick}
                      disabled={!templatePreviewReady}
                      endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      sx={{
                        minWidth: { xs: '100%', sm: 120 },
                        position: 'relative',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '14px',
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 1.5,
                      background: COLORS.PRIMARY_GRADIENT,
                      boxShadow: '0 4px 15px rgba(145, 27, 124, 0.4)',
                      '&:hover': {
                        background: COLORS.PRIMARY_HOVER_GRADIENT,
                        boxShadow: '0 6px 20px rgba(145, 27, 124, 0.5)',
                        transform: 'translateY(-1px)',
                      },
                      '&:active': { transform: 'translateY(0)' },
                      '&:disabled': { background: 'grey.400', boxShadow: 'none' },
                    }}
                  >
                    Next
                    {hasUnsavedChanges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                        }}
                      />
                    )}
                  </Button>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <Box component="span" sx={{ order: 2 }}>
                <Tooltip
                  title={
                    !templatePreviewReady
                      ? MESSAGES.INFO.LOADING_PREVIEW
                      : hasUnsavedChanges
                        ? MESSAGES.INFO.UNSAVED_CHANGES_SEND
                        : ''
                  }
                  arrow
                >
                  <span>
                  <LoadingButton
                    variant="contained"
                    onClick={handleSendClick}
                    loading={loading}
                    disabled={isEditRecipient !== null || hasUnsavedChanges || !templatePreviewReady}
                    startIcon={<Send sx={{ fontSize: 16 }} />}
                    sx={{
                      minWidth: { xs: '100%', sm: 140 },
                      position: 'relative',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1.5,
                    background: COLORS.SUCCESS_GRADIENT,
                    boxShadow: '0 4px 15px rgba(46, 125, 50, 0.4)',
                    '&:hover': {
                      background: COLORS.SUCCESS_HOVER_GRADIENT,
                      boxShadow: '0 6px 20px rgba(46, 125, 50, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'translateY(0)' },
                    '&:disabled': { background: 'grey.400', boxShadow: 'none' },
                  }}
                >
                  Send Emails
                  {hasUnsavedChanges && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'warning.main',
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                      }}
                    />
                  )}
                  </LoadingButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {responseSummary?.open && (
        <EmailResponseSummary
          open={responseSummary.open}
          onClose={handleCloseResponseSummary}
          data={responseSummary.data?.data || null}
          addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
        />
      )}

      <EmailSendingModal
        open={loading}
        progress={emailSendingProgress.progressPercentage}
        message={emailSendingProgress.message}
        totalEmails={emailSendingProgress.totalEmails}
        sentEmails={emailSendingProgress.sentEmails}
      />
    </>
  );
};

export default SendEmailModalWrapper;
